import {Entity} from "@ots-frontend/ots-data";
import {ReactNode, useMemo} from "react";
import {ProtectedPage, ProtectedPageProps} from "../../components/page/ProtectedPage/ProtectedPage";
import {Container} from "@mantine/core";
import {useTranslation} from "react-i18next";

type Props<T extends Entity> = Omit<ProtectedPageProps, 'children' | 'permissions'> & {
  entity?: T,
  permissions?: string | string[] | ((entity: T | undefined) => string[])
  children?: ((entity: T) => ReactNode | undefined) | ReactNode | undefined
}

export function EntityProfilePage<T extends Entity>(props: Props<T>) {
  const {entity, permissions = [], error, children, ...rest} = props;

  const { t } = useTranslation(["common"])

  const requiredPermissions = useMemo(() => {
    if (permissions) {
      if (typeof permissions === 'function') {
        return permissions(entity)
      } else if (typeof permissions === 'string') {
        return [permissions]
      } else {
        return permissions
      }
    }

    return []
  }, [permissions, entity])

  const _error = useMemo(() => {
    if (error) {
      return error
    }

    if (!entity) {
      return t("common.error.notfound", "Not Found")
    }

    return undefined
  }, [error, entity, t])

  return (
    <ProtectedPage{...rest} permissions={requiredPermissions} error={_error}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          {children instanceof Function ? children(entity) : children}
        </Container>
      )}
    </ProtectedPage>
  )
}
